import { useMemo } from "react";
import { Linkedin, Facebook, Instagram } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { KLogo } from "@/components/KLogo";

const NAV = [
  { key: "home", href: "#accueil" },
  { key: "about", href: "#a-propos" },
  { key: "services", href: "#services" },
  { key: "contact", href: "#contact" },
];

const SOCIAL_ICONS = [
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "instagram", label: "Instagram", Icon: Instagram },
];

export const Footer = () => {
  const { t } = useLang();
  const visibleSocials = useMemo(() => {
    const s = t?.contact?.socials || {};
    return SOCIAL_ICONS.filter(({ key }) => s[key]);
  }, [t]);
  if (!t) return null;
  const { footer, nav, contact } = t;

  return (
    <footer className="bg-noir text-creme" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid gap-12 md:grid-cols-[1.5fr_1fr_1.2fr]">
        <div>
          <KLogo white className="h-10 w-auto mb-5" />
          <p className="text-sm text-creme/60 max-w-xs leading-relaxed">{footer.tagline}</p>
          <div className="flex items-center gap-3 mt-6" data-testid="footer-social">
            {visibleSocials.map(({ key, label, Icon }) => (
              <a key={key} href={t.contact.socials[key]} aria-label={label} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${key}`} className="h-9 w-9 grid place-items-center rounded-full border border-creme/20 hover:border-rouge hover:text-rouge transition-colors">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        <div data-testid="footer-nav">
          <h4 className="text-xs uppercase tracking-[0.2em] text-or mb-5">{footer.nav_title}</h4>
          <ul className="space-y-3">
            {NAV.map((item) => (
              <li key={item.key}>
                <a href={item.href} className="text-sm text-creme/70 hover:text-creme transition-colors">
                  {nav[item.key]}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-or mb-5">{footer.contact_title}</h4>
          <ul className="space-y-3 text-sm text-creme/70">
            <li><a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="hover:text-creme">{contact.phone}</a></li>
            <li><a href={`mailto:${contact.email}`} className="hover:text-creme">{contact.email}</a></li>
            <li>{contact.location}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-creme/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-creme/45">
          <span>© {new Date().getFullYear()} {footer.copyright}</span>
          <a href="/admin" className="hover:text-creme/80 transition-colors" data-testid="footer-admin-link">Admin</a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
